const HERO_TYPE_BOUNDS = {
  locked: {
    desktop: { headlineMax: 42, headlineMin: 18, bodyMax: 17, bodyMin: 13 },
    mobile: { headlineMax: 24, headlineMin: 16, bodyMax: 15, bodyMin: 12 },
  },
  fluid: {
    desktop: { headlineMax: 58, headlineMin: 20, bodyMax: 22, bodyMin: 13 },
    mobile: { headlineMax: 28, headlineMin: 17, bodyMax: 16, bodyMin: 12 },
  },
};

const HERO_DRAG_THRESHOLD_PX = 48;
const HERO_DRAG_AXIS_RATIO = 1.25;

class HomeHeroSlider extends HTMLElement {
  connectedCallback() {
    this.slides = Array.from(this.querySelectorAll("[data-slide]"));
    this.selectors = Array.from(this.querySelectorAll("[data-selector]"));
    this.slidesEl = this.querySelector(".home-hero__slides");
    this.currentIndex = 0;
    this.autoplay = this.dataset.autoplay === "true";
    this.intervalMs = Number(this.dataset.interval) || 5000;
    this.timer = null;
    this._fitRaf = null;
    this._drag = null;
    this._abort = new AbortController();

    if (!this.slides.length) return;

    this.bindEvents();
    this.bindDragSwipe();
    this.goToSlide(0);
    this.startAutoplay();

    const { signal } = this._abort;
    requestAnimationFrame(() => this.scheduleContentFit());
    window.addEventListener("resize", () => this.scheduleContentFit(), { signal });
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(() => this.scheduleContentFit());
    }
  }

  disconnectedCallback() {
    this.stopAutoplay();
    this._abort.abort();
  }

  scheduleContentFit() {
    if (this._fitRaf != null) return;
    this._fitRaf = requestAnimationFrame(() => {
      this._fitRaf = null;
      this.fitAllSlideContent();
      this.positionAllSlideBackgrounds();
    });
  }

  readSlidePercent(slide, cssVar, fallback) {
    const raw = getComputedStyle(slide).getPropertyValue(cssVar).trim();
    const value = parseFloat(raw);
    return Number.isFinite(value) ? value : fallback;
  }

  getDisplayedImageSize(cw, ch, iw, ih, mode) {
    if (mode === "width") {
      return { width: cw, height: cw * (ih / iw) };
    }
    if (mode === "height") {
      return { width: ch * (iw / ih), height: ch };
    }
    const scale = Math.max(cw / iw, ch / ih);
    return { width: iw * scale, height: ih * scale };
  }

  computeAnchorPosition(cw, ch, iw, ih, focalX, focalY, anchorX, anchorY, mode) {
    const displayed = this.getDisplayedImageSize(cw, ch, iw, ih, mode);
    const focalPxX = (focalX / 100) * displayed.width;
    const focalPxY = (focalY / 100) * displayed.height;
    const anchorPxX = (anchorX / 100) * cw;
    const anchorPxY = (anchorY / 100) * ch;
    return {
      x: anchorPxX - focalPxX,
      y: anchorPxY - focalPxY,
      backgroundSize:
        mode === "width" ? "100% auto" : mode === "height" ? "auto 100%" : `${displayed.width}px ${displayed.height}px`,
    };
  }

  alignSlideBackground(slide) {
    const isDesktop = window.matchMedia("(min-width: 990px)").matches;
    const bg = slide.querySelector(".home-hero__background--desktop-bg");
    const desktopImg = slide.querySelector(".home-hero__image--desktop");
    const visibleImg =
      slide.querySelector(".home-hero__image--mobile") ||
      slide.querySelector(".home-hero__image:not(.home-hero__image--placeholder):not(.home-hero__image--desktop)");

    if (isDesktop && bg && desktopImg) {
      const mode = bg.classList.contains("home-hero__background--d-height") ? "height" : "width";
      const applyDesktop = () => {
        const box = bg;
        const cw = box.clientWidth;
        const ch = box.clientHeight;
        const iw = desktopImg.naturalWidth;
        const ih = desktopImg.naturalHeight;
        if (!cw || !ch || !iw || !ih) return;

        const focalX = this.readSlidePercent(slide, "--hero-focal-x-desktop", 50);
        const focalY = this.readSlidePercent(slide, "--hero-focal-y-desktop", 50);
        const anchorX = this.readSlidePercent(slide, "--hero-anchor-x-desktop", 50);
        const anchorY = this.readSlidePercent(slide, "--hero-anchor-y-desktop", 50);
        const pos = this.computeAnchorPosition(cw, ch, iw, ih, focalX, focalY, anchorX, anchorY, mode);

        bg.style.backgroundSize = pos.backgroundSize;
        bg.style.backgroundPosition = `${pos.x}px ${pos.y}px`;
        desktopImg.style.removeProperty("object-position");
      };

      if (desktopImg.complete) applyDesktop();
      else desktopImg.addEventListener("load", applyDesktop, { once: true });
      return;
    }

    if (bg) {
      bg.style.removeProperty("background-position");
      bg.style.removeProperty("background-size");
    }

    if (!isDesktop && visibleImg) {
      const applyMobile = () => {
        const box = visibleImg.parentElement;
        const cw = box.clientWidth;
        const ch = box.clientHeight;
        const iw = visibleImg.naturalWidth;
        const ih = visibleImg.naturalHeight;
        if (!cw || !ch || !iw || !ih) return;

        const focalX = this.readSlidePercent(slide, "--hero-focal-x-mobile", 50);
        const focalY = this.readSlidePercent(slide, "--hero-focal-y-mobile", 50);
        const anchorX = this.readSlidePercent(slide, "--hero-anchor-x-mobile", 50);
        const anchorY = this.readSlidePercent(slide, "--hero-anchor-y-mobile", 50);
        const pos = this.computeAnchorPosition(cw, ch, iw, ih, focalX, focalY, anchorX, anchorY, "cover");

        visibleImg.style.objectFit = "cover";
        visibleImg.style.objectPosition = `${pos.x}px ${pos.y}px`;
      };

      if (visibleImg.complete) applyMobile();
      else visibleImg.addEventListener("load", applyMobile, { once: true });
    }
  }

  positionAllSlideBackgrounds() {
    this.slides.forEach((slide) => this.alignSlideBackground(slide));
  }

  getTypeBounds() {
    const locked = this.dataset.lockVisual === "true";
    const mode = locked ? "locked" : "fluid";
    const bp = window.matchMedia("(min-width: 990px)").matches ? "desktop" : "mobile";
    return HERO_TYPE_BOUNDS[mode][bp];
  }

  resetType(content) {
    content.querySelectorAll(".home-hero__headline, .home-hero__text").forEach((el) => {
      el.style.removeProperty("font-size");
    });
  }

  fitsBox(content) {
    return content.scrollHeight <= content.clientHeight + 1 && content.scrollWidth <= content.clientWidth + 1;
  }

  fitSlideContent(content) {
    const headline = content.querySelector(".home-hero__headline");
    const body = content.querySelector(".home-hero__text");
    if (!headline && !body) return;

    const boxH = content.clientHeight;
    const boxW = content.clientWidth;
    if (boxH < 48 || boxW < 120) return;

    const bounds = this.getTypeBounds();
    this.resetType(content);
    void content.offsetHeight;

    let headMax = bounds.headlineMax;
    let bodyMax = bounds.bodyMax;
    if (headline) {
      const computed = parseFloat(getComputedStyle(headline).fontSize);
      if (Number.isFinite(computed) && computed > 0) headMax = Math.min(headMax, Math.round(computed));
    }
    if (body) {
      const computed = parseFloat(getComputedStyle(body).fontSize);
      if (Number.isFinite(computed) && computed > 0) bodyMax = Math.min(bodyMax, Math.round(computed));
    }

    for (let h = headMax; h >= bounds.headlineMin; h -= 1) {
      if (headline) headline.style.fontSize = h + "px";
      for (let b = bodyMax; b >= bounds.bodyMin; b -= 1) {
        if (body) body.style.fontSize = b + "px";
        void content.offsetHeight;
        if (this.fitsBox(content)) return;
      }
    }

    if (headline) headline.style.fontSize = bounds.headlineMin + "px";
    if (body) body.style.fontSize = bounds.bodyMin + "px";
  }

  fitAllSlideContent() {
    this.slides.forEach((slide) => {
      if (!slide.classList.contains("is-active")) return;
      const content = slide.querySelector(".home-hero__content");
      if (content) this.fitSlideContent(content);
    });
  }

  isInteractiveDragTarget(target) {
    if (!(target instanceof Element)) return false;
    return Boolean(target.closest("a, button, input, textarea, select, label, [role='tab']"));
  }

  bindDragSwipe() {
    if (!this.slidesEl || this.slides.length < 2) return;

    const signal = this._abort.signal;

    const onPointerDown = (event) => {
      if (event.pointerType === "mouse" && event.button !== 0) return;
      if (this.isInteractiveDragTarget(event.target)) return;

      this._drag = {
        pointerId: event.pointerId,
        startX: event.clientX,
        startY: event.clientY,
        axisLocked: false,
        cancelled: false,
      };

      this.slidesEl.setPointerCapture(event.pointerId);
      this.slidesEl.classList.add("is-dragging");
      this.stopAutoplay();
    };

    const onPointerMove = (event) => {
      if (!this._drag || event.pointerId !== this._drag.pointerId || this._drag.cancelled) return;

      const deltaX = event.clientX - this._drag.startX;
      const deltaY = event.clientY - this._drag.startY;
      const absX = Math.abs(deltaX);
      const absY = Math.abs(deltaY);

      if (!this._drag.axisLocked) {
        if (absX < HERO_DRAG_THRESHOLD_PX && absY < HERO_DRAG_THRESHOLD_PX) return;
        if (absX > absY * HERO_DRAG_AXIS_RATIO) {
          this._drag.axisLocked = true;
        } else if (absY > absX * HERO_DRAG_AXIS_RATIO) {
          this._drag.cancelled = true;
          return;
        } else {
          return;
        }
      }

      event.preventDefault();
    };

    const onPointerEnd = (event) => {
      if (!this._drag || event.pointerId !== this._drag.pointerId) return;

      const deltaX = event.clientX - this._drag.startX;
      const wasLocked = this._drag.axisLocked && !this._drag.cancelled;

      try {
        this.slidesEl.releasePointerCapture(event.pointerId);
      } catch (_error) {
        /* capture may already be released */
      }

      this.slidesEl.classList.remove("is-dragging");
      this._drag = null;

      if (wasLocked && Math.abs(deltaX) >= HERO_DRAG_THRESHOLD_PX) {
        if (deltaX < 0) this.nextSlide();
        else this.prevSlide();
        this.restartAutoplay();
        return;
      }

      this.startAutoplay();
    };

    this.slidesEl.addEventListener("pointerdown", onPointerDown, { signal });
    this.slidesEl.addEventListener("pointermove", onPointerMove, { signal, passive: false });
    this.slidesEl.addEventListener("pointerup", onPointerEnd, { signal });
    this.slidesEl.addEventListener("pointercancel", onPointerEnd, { signal });
  }

  bindEvents() {
    const signal = this._abort.signal;

    this.selectors.forEach((selector) => {
      selector.addEventListener(
        "click",
        () => {
          const index = Number(selector.dataset.slideIndex);
          this.goToSlide(index);
          this.restartAutoplay();
        },
        { signal }
      );
    });

    this.addEventListener("mouseenter", () => this.stopAutoplay(), { signal });
    this.addEventListener("mouseleave", () => this.startAutoplay(), { signal });
    this.addEventListener("focusin", () => this.stopAutoplay(), { signal });
    this.addEventListener("focusout", () => this.startAutoplay(), { signal });
  }

  goToSlide(index) {
    this.currentIndex = index;

    this.slides.forEach((slide, slideIndex) => {
      const isActive = slideIndex === index;
      slide.classList.toggle("is-active", isActive);
      slide.setAttribute("aria-hidden", isActive ? "false" : "true");
    });

    this.selectors.forEach((selector, selectorIndex) => {
      const isActive = selectorIndex === index;
      selector.classList.toggle("is-active", isActive);
      selector.setAttribute("aria-selected", isActive ? "true" : "false");
    });

    this.scheduleContentFit();
  }

  nextSlide() {
    const nextIndex = (this.currentIndex + 1) % this.slides.length;
    this.goToSlide(nextIndex);
  }

  prevSlide() {
    const prevIndex = (this.currentIndex - 1 + this.slides.length) % this.slides.length;
    this.goToSlide(prevIndex);
  }

  startAutoplay() {
    if (!this.autoplay || this.slides.length < 2 || this.timer) return;

    this.timer = window.setInterval(() => {
      this.nextSlide();
    }, this.intervalMs);
  }

  stopAutoplay() {
    if (!this.timer) return;
    window.clearInterval(this.timer);
    this.timer = null;
  }

  restartAutoplay() {
    this.stopAutoplay();
    this.startAutoplay();
  }
}

customElements.define("home-hero-slider", HomeHeroSlider);
